// Updated Level2AuthModal: store token + authLevel in localStorage and call onAuthSuccess(newLevel, token)
// - onAuthSuccess signature changed to (newLevel: number, token?: string) to match ScanConsumerPage usage.
// - Persist token under 'consumerToken' and auth level under 'authLevel' so other components read them.
// - Calls onAuthError on failures as before.

import React, { useEffect, useRef, useState } from 'react';
import { User, Mail, Lock, Phone, ArrowRight, X, Shield, Star, CheckCircle } from 'lucide-react';

declare global {
  interface Window {
    google?: any;
  }
}

interface Level2AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  // NOTE: changed signature to be (newLevel, token?) to match ScanConsumerPage handleAuthSuccess
  onAuthSuccess: (newLevel: number, token?: string) => void;
  onAuthError: (error: string) => void;
}

interface FormData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  confirmPassword: string;
  agreeToTerms: boolean;
  marketingOptIn: boolean;
}

interface FormErrors {
  email?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  confirmPassword?: string;
  agreeToTerms?: string;
  general?: string;
}

// Use your .env base API URL
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const Level2AuthModal: React.FC<Level2AuthModalProps> = ({
  isOpen,
  onClose,
  onAuthSuccess,
  onAuthError,
}) => {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    confirmPassword: '',
    agreeToTerms: false,
    marketingOptIn: false,
  });

  const googleBtnRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        phone: '',
        confirmPassword: '',
        agreeToTerms: false,
        marketingOptIn: false,
      });
      setErrors({});
      setLoading(false);
    }
  }, [isOpen]);

  // Google Identity setup
  useEffect(() => {
    if (!isOpen || !GOOGLE_CLIENT_ID) return;

    function renderGoogleButton() {
      if (window.google && googleBtnRef.current) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleSignIn,
          auto_select: false,
        });

        window.google.accounts.id.renderButton(googleBtnRef.current, {
          type: 'standard',
          shape: 'pill',
          size: 'large',
          text: 'continue_with',
          logo_alignment: 'left',
          width: 280,
        });
      }
    }

    if (!window.google) {
      if (!document.getElementById('google-signin-script')) {
        const script = document.createElement('script');
        script.id = 'google-signin-script';
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = renderGoogleButton;
        document.head.appendChild(script);
      }
    } else {
      renderGoogleButton();
    }
  }, [isOpen, GOOGLE_CLIENT_ID]);

  // Google Sign-In handler
  const handleGoogleSignIn = async (credentialResponse: any) => {
    try {
      setLoading(true);
      setErrors({});
      const credential = credentialResponse?.credential;
      if (!credential) {
        throw new Error('No Google credential received');
      }
      const res = await fetch(`${API_BASE}/auth/consumer/google-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        // body: JSON.stringify({ credential }),
        body: JSON.stringify({ credential }),
      });
      const result = await res.json();

      if (result.success && (result.consumer || result.token)) {
        // Persist token and level for other components
        if (result.token) {
          try {
            localStorage.setItem('consumerToken', result.token);
            localStorage.setItem('authLevel', '2');
          } catch (e) {
            console.warn('Failed to persist login token to localStorage', e);
          }
        }

        // Notify parent with standardized signature (newLevel, token)
        onAuthSuccess(2, result.token);
        onClose();
      } else {
        throw new Error(result.message || 'Google authentication failed');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Google authentication failed';
      setErrors({ general: errorMessage });
      onAuthError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Simple form validation
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    if (mode === 'register') {
      if (!formData.firstName.trim()) {
        newErrors.firstName = 'First name is required';
      }
      if (!formData.lastName.trim()) {
        newErrors.lastName = 'Last name is required';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      if (!formData.agreeToTerms) {
        newErrors.agreeToTerms = 'You must agree to the terms and conditions';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setLoading(true);
      setErrors({});
      let apiUrl = `${API_BASE}/auth/consumer/login`;
      let payload: any = {
        email: formData.email,
        password: formData.password,
      };
      if (mode === 'register') {
        apiUrl = `${API_BASE}/auth/consumer/register`;
        payload = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          phone: formData.phone || undefined,
          marketingOptIn: formData.marketingOptIn,
        };
      }
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const result = await res.json();

      if (result.success && (result.consumer || result.token)) {
        // Persist token and auth level for other components/pages
        if (result.token) {
          try {
            localStorage.setItem('consumerToken', result.token);
            localStorage.setItem('authLevel', '2');
          } catch (e) {
            console.warn('Failed to persist login token to localStorage', e);
          }
        }

        // Notify parent (standardized signature)
        onAuthSuccess(2, result.token);
        onClose();
      } else {
        throw new Error(result.message || `${mode === 'login' ? 'Login' : 'Registration'} failed`);
      }
    } catch (error) {
      let errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.';
      setErrors({ general: errorMessage });
      onAuthError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Left Side - Benefits */}
          <div className="bg-gradient-to-br from-blue-600 to-purple-700 p-8 text-white rounded-l-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Shield className="w-6 h-6" />
                <h2 className="text-2xl font-bold">Level 2 Access</h2>
              </div>
              <button onClick={onClose} className="text-white/80 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-lg mb-8 text-blue-100">
              Unlock advanced product verification and exclusive rewards
            </p>
            <div className="space-y-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold mb-1">Complete Supply Chain Traceability</h3>
                  <p className="text-sm text-blue-100">
                    View full manufacturing history, batch details, and quality certificates
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold mb-1">Loyalty Points & Rewards</h3>
                  <p className="text-sm text-blue-100">
                    Earn points for every scan and redeem exclusive rewards
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold mb-1">Personal Scan History</h3>
                  <p className="text-sm text-blue-100">
                    Track all your verified products in one secure dashboard
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Star className="w-5 h-5 text-yellow-400 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold mb-1">Premium Support</h3>
                  <p className="text-sm text-blue-100">
                    Priority customer service and direct business contact
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Right Side - Auth Form */}
          <div className="p-8">
            <div className="mb-6">
              <div className="flex border-b">
                <button
                  onClick={() => setMode('login')}
                  disabled={loading}
                  className={`pb-3 px-4 font-medium ${
                    mode === 'login' 
                      ? 'border-b-2 border-blue-600 text-blue-600' 
                      : 'text-gray-500'
                  } ${loading ? 'opacity-50' : ''}`}
                >
                  Sign In
                </button>
                <button
                  onClick={() => setMode('register')}
                  disabled={loading}
                  className={`pb-3 px-4 font-medium ${
                    mode === 'register' 
                      ? 'border-b-2 border-blue-600 text-blue-600' 
                      : 'text-gray-500'
                  } ${loading ? 'opacity-50' : ''}`}
                >
                  Create Account
                </button>
              </div>
            </div>
            {errors.general && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{errors.general}</p>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'register' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={formData.firstName}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                          disabled={loading}
                          className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                            errors.firstName ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="John"
                        />
                      </div>
                      {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={formData.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          disabled={loading}
                          className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                            errors.lastName ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Doe"
                        />
                      </div>
                      {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
                    </div>
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    disabled={loading}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="john@example.com"
                  />
                </div>
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
              </div>
              {mode === 'register' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number (Optional)</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      disabled={loading}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    disabled={loading}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      errors.password ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your password"
                  />
                </div>
                {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
              </div>
              {mode === 'register' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="password"
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        disabled={loading}
                        className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                          errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Confirm your password"
                      />
                    </div>
                    {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
                  </div>
                  <div className="space-y-3">
                    <label className="flex items-start">
                      <input
                        type="checkbox"
                        checked={formData.agreeToTerms}
                        onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                        disabled={loading}
                        className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1 ${
                          errors.agreeToTerms ? 'border-red-500' : ''
                        }`}
                      />
                      <span className="ml-2 text-sm text-gray-600">
                        I agree to the{' '}
                        <a href="#" className="text-blue-600 hover:underline">Terms of Service</a>{' '}
                        and{' '}
                        <a href="#" className="text-blue-600 hover:underline">Privacy Policy</a>
                      </span>
                    </label>
                    {errors.agreeToTerms && <p className="text-sm text-red-600">{errors.agreeToTerms}</p>}
                    <label className="flex items-start">
                      <input
                        type="checkbox"
                        checked={formData.marketingOptIn}
                        onChange={(e) => handleInputChange('marketingOptIn', e.target.checked)}
                        disabled={loading}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1"
                      />
                      <span className="ml-2 text-sm text-gray-600">
                        Send me product updates and promotional offers
                      </span>
                    </label>
                  </div>
                </>
              )}
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                  loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    {mode === 'login' ? 'Sign In' : 'Create Account'}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
            {/* Google Sign-In */}
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or continue with</span>
                </div>
              </div>
              <div className="mt-6 flex justify-center">
                {GOOGLE_CLIENT_ID ? (
                  <div ref={googleBtnRef} className={loading ? 'opacity-50 pointer-events-none' : ''} />
                ) : (
                  <div className="text-center text-sm text-gray-500">
                    Google Sign-in disabled
                  </div>
                )}
              </div>
            </div>
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
                <button
                  onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
                  disabled={loading}
                  className="text-blue-600 hover:underline font-medium"
                >
                  {mode === 'login' ? 'Sign up' : 'Sign in'}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Level2AuthModal;